import React, {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  Text, View, ScrollView, StyleSheet, Animated, Keyboard, Pressable,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useDispatch, useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../constants/Colors';
import DateComponent from '../../../../components/DateComponent';
import Switch from '../../../../components/Switch';
import { Area, Floor, Space } from '../../../../components/Map/SetFloorsModal/FloorItem';
import isMobile from '../../../../components/IsMobile';
import FloorDropdown from './components/FloorDropdown';
import AreaDropdown from './components/AreaDropdown';
import SpaceDropdown from './components/SpaceDropdown';
import AssigneeDropdown from './components/AssigneeDropdown';
import AssetDropdown from './components/AssetDropdown';
import UserGroupDropdown from './components/UserGroupDropdown';
import CheckBoxItem from '../../../../components/CheckBoxItem';
import { DateRange, DateSelectionRange } from '../../../../redux/dateRangeSlice';
import { logEvent } from '../../../../components/LogEvent';
import Style from '../../../../constants/Style';
import { updateTicketsFilter } from '../../../../redux/ticketsFilterSlice';
import DeleteIcon24 from '../../../../components/24pxIcons/DeleteIcon24';
import PlusIcon20 from '../../../../components/24pxIcons/PlusIcon';
import AscendingIcon from '../../../../components/16pxIcons/AscendingIcon';
import DescendingIcon from '../../../../components/16pxIcons/DescendingIcon';
import {
  AssetInstance, Template, Ticket, UserGroup, UserInstance,
} from '../../../../constants/Types';
import PressableStyled from '../../../../components/PressableStyled';
import TemplateDropdown from './components/TemplateDropdown';
import IssueDropdown from './components/IssueDropdown';
import ResetFiltersButton from '../../../../components/ResetFiltersButton';

interface TicketsFilterProps {
  otherPriorityFilter: boolean,
  lowPriorityFilter: boolean,
  mediumPriorityFilter: boolean,
  highPriorityFilter: boolean,
  criticalPriorityFilter: boolean,
  doneFilter?: boolean,
  inProgressFilter?: boolean,
  notStartedFilter?: boolean,
  slaOverdueFilter?: boolean,
  slaSort: string,
  selectedAssignee: string,
  selectedAssigneeName: string,
  selectedAssetId: string,
  selectedAssetName?: string,
  selectedUserGroupId: string,
  selectedLocation: string,
  assets: AssetInstance[],
  templates?: Template[],
  users: UserInstance[],
  userGroups: UserGroup[],
  setFilterOpen: Dispatch<SetStateAction<boolean>>,
  setTickets: Dispatch<SetStateAction<Ticket[]>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setSelectedTicket: Dispatch<SetStateAction<Ticket>>,
  setOtherPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setLowPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setMediumPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setHighPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setCriticalPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setDoneFilter?: Dispatch<SetStateAction<boolean>>,
  setInProgressFilter?: Dispatch<SetStateAction<boolean>>,
  setNotStartedFilter?: Dispatch<SetStateAction<boolean>>,
  setSlaOverdueFilter?: Dispatch<SetStateAction<boolean>>,
  setSlaSort: Dispatch<SetStateAction<string>>,
  setSelectedAssignee: Dispatch<SetStateAction<string>>,
  setSelectedAssigneeName: Dispatch<SetStateAction<string>>,
  setSelectedAssetName?: Dispatch<SetStateAction<string>>,
  setSelectedAssetId: Dispatch<SetStateAction<string>>,
  setSelectedUserGroupId: Dispatch<SetStateAction<string>>,
  setSelectedTemplateId?: Dispatch<SetStateAction<string>>,
  setSelectedIssueId?: Dispatch<SetStateAction<string>>,
  setSelectedLocation: Dispatch<SetStateAction<string>>,
  setSelectedFloor: Dispatch<SetStateAction<Floor>>,
  range: DateRange,
  selectedRange?: DateSelectionRange | null,
  onConfirm?,
  setSelectedRange?: Dispatch<SetStateAction<number>> | null,
  slaNames: string[],
  filterShown: boolean,
  saveFilterToggled: boolean,
  setSaveFilterToggled: Dispatch<SetStateAction<boolean>>,
  hideDatePicker?: boolean,
  floorSelectionDisabled?: boolean,
  selectedTemplateId?: string,
  selectedIssueId?: string,
  resetAllFilters?: any;
}

export default function TicketsFilter(props: TicketsFilterProps) {
  const width: number = 526;
  const [otherPriorityFilter,
    setOtherPriorityFilter] = useState<boolean>(props.otherPriorityFilter);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<boolean>(props.lowPriorityFilter);
  const [mediumPriorityFilter,
    setMediumPriorityFilter] = useState<boolean>(props.mediumPriorityFilter);
  const [highPriorityFilter, setHighPriorityFilter] = useState<boolean>(props.highPriorityFilter);
  const [criticalPriorityFilter,
    setCriticalPriorityFilter] = useState<boolean>(props.criticalPriorityFilter);
  const [doneFilter, setDoneFilter] = useState<boolean>(props.doneFilter);
  const [inProgressFilter, setInProgressFilter] = useState<boolean>(props.inProgressFilter);
  const [notStartedFilter, setNotStartedFilter] = useState<boolean>(props.notStartedFilter);
  const [slaOverdueFilter, setSlaOverdueFilter] = useState<boolean>(props.slaOverdueFilter);
  const [slaSort, setSlaSort] = useState<string>(props.slaSort);
  const [selectedAssignee, setSelectedAssignee] = useState<string>(props.selectedAssignee);
  const [selectedAssigneeName,
    setSelectedAssigneeName] = useState<string>(props.selectedAssigneeName);
  const [selectedAssetId, setSelectedAssetId] = useState<string>(props.selectedAssetId);
  const [selectedAssetName, setSelectedAssetName] = useState<string>(props.selectedAssetName);
  const [selectedUserGroupId,
    setSelectedUserGroupId] = useState<string>(props.selectedUserGroupId);
  const [selectedTemplateId,
    setSelectedTemplateId] = useState<string>(props.selectedTemplateId);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(props.selectedLocation);
  const [selectedIssueId, setSelectedIssueId] = useState<string>(props.selectedIssueId);
  const [assigneeDropdownShown, setAssigneeDropdownShown] = useState<boolean>(false);
  const [assetDropdownShown, setAssetDropdownShown] = useState<boolean>(false);
  const [userGroupDropdownShown, setUserGroupDropdownShown] = useState<boolean>(false);
  const [templateDropdownShown, setTemplateDropdownShown] = useState<boolean>(false);
  const [floorDropdownShown, setFloorDropdownShown] = useState<boolean>(false);
  const [areaDropdownShown, setAreaDropdownShown] = useState<boolean>(false);
  const [spaceDropdownShown, setSpaceDropdownShown] = useState<boolean>(false);
  const [issueDropdownShown, setIssueDropdownShown] = useState<boolean>(false);
  const [selectedFloor, setSelectedFloor] = useState<Floor>(null);
  const [selectedArea, setSelectedArea] = useState<Area>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space>(null);
  const [userGroupSearchValue, setUserGroupSearchValue] = useState<string>('');
  const [assetSearchValue, setAssetSearchValue] = useState<string>('');
  const scrollView = useRef<ScrollView>(null);
  const [filterDrawerInset] = useState(new Animated.Value(width));
  const [saveFilterToggled, setSaveFilterToggled] = useState<boolean>(props.saveFilterToggled);
  const [dateRange, setDateRange] = useState<DateSelectionRange>(1);
  const [date, setDate] = useState<DateRange>({ start: props.range.start, end: props.range.end });
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);

  const dispatch = useDispatch();

  useEffect(() => {
    if (floorDropdownShown || areaDropdownShown || spaceDropdownShown || userGroupDropdownShown) {
      scrollView.current?.scrollToEnd();
    }
  }, [floorDropdownShown, areaDropdownShown, spaceDropdownShown, userGroupDropdownShown]);

  useEffect(() => {
    if (!(buildings && selectedBuildingId && props.selectedLocation)) return;
    Object.values(buildings[selectedBuildingId]?.floors ?? {}).forEach((floor: Floor | null) => {
      if (!floor) return;
      if (floor?.LocationId === props.selectedLocation) {
        setSelectedFloor(floor);
      } else {
        Object.values(floor?.areas ?? {}).forEach((area: Area | null) => {
          if (!area) return;
          if (area?.LocationId === props.selectedLocation) {
            setSelectedFloor(floor);
            setSelectedArea(area);
          } else {
            Object.values(area?.spaces ?? {}).forEach((space: Space) => {
              if (!space) return;
              if (space.LocationId === props.selectedLocation) {
                setSelectedFloor(floor);
                setSelectedArea(area);
                setSelectedSpace(space);
              }
            });
          }
        });
      }
    });
  }, [buildings, selectedBuildingId, props.selectedLocation]);

  useEffect(() => setOtherPriorityFilter(props.otherPriorityFilter), [props.otherPriorityFilter]);
  useEffect(() => setLowPriorityFilter(props.lowPriorityFilter), [props.lowPriorityFilter]);
  useEffect(
    () => setMediumPriorityFilter(props.mediumPriorityFilter),
    [props.mediumPriorityFilter],
  );
  useEffect(() => setHighPriorityFilter(props.highPriorityFilter), [props.highPriorityFilter]);
  useEffect(
    () => setCriticalPriorityFilter(props.criticalPriorityFilter),
    [props.criticalPriorityFilter],
  );
  useEffect(() => setDoneFilter(props.doneFilter), [props.doneFilter]);
  useEffect(() => setNotStartedFilter(props.notStartedFilter), [props.notStartedFilter]);
  useEffect(() => setInProgressFilter(props.inProgressFilter), [props.inProgressFilter]);
  useEffect(() => setSelectedAssetId(props.selectedAssetId), [props.selectedAssetId]);
  useEffect(() => setSelectedAssetName(props.selectedAssetName), [props.selectedAssetName]);
  useEffect(() => setSelectedAssignee(props.selectedAssignee), [props.selectedAssignee]);
  useEffect(() => setSelectedUserGroupId(props.selectedUserGroupId), [props.selectedUserGroupId]);
  useEffect(() => setSelectedLocationId(props.selectedLocation), [props.selectedLocation]);
  useEffect(() => setSaveFilterToggled(props.saveFilterToggled), [props.saveFilterToggled]);
  useEffect(() => setSlaSort(props.slaSort), [props.slaSort]);

  useEffect(() => {
    if (props.filterShown) {
      Keyboard.dismiss();
      Animated.timing(filterDrawerInset, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(filterDrawerInset, {
        toValue: width,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [props.filterShown]);

  const anyFilterApplied: boolean = otherPriorityFilter || lowPriorityFilter || mediumPriorityFilter
    || highPriorityFilter || criticalPriorityFilter
    || (props.setDoneFilter && doneFilter) || (props.setNotStartedFilter && notStartedFilter)
    || (props.setInProgressFilter && inProgressFilter)
    || (props.setSlaOverdueFilter && slaOverdueFilter)
    || !!selectedAssignee || !!selectedAssetId || !!selectedUserGroupId
    || (props.setSelectedLocation && !!selectedFloor);

  const datePicker = () => <View style={styles.datePickerContainer}>
    <DateComponent
      onChange={(start, end) => setDate({ start, end })}
      onCompareChange={() => { }}
      onOpen={() => { }}
      renderLeft={!isMobile}
      onConfirm={() => { }}
      startDate={date.start}
      endDate={date.end}
      selectedRange={props.selectedRange}
      setSelectedRange={setDateRange}
    />
  </View>;

  const closeDropdowns: () => void = () => {
    setAssetDropdownShown(false);
    setAssigneeDropdownShown(false);
    setUserGroupDropdownShown(false);
    setFloorDropdownShown(false);
    setAreaDropdownShown(false);
    setSpaceDropdownShown(false);
  };

  const resetFilters: () => void = () => {
    props.setOtherPriorityFilter(false);
    props.setLowPriorityFilter(false);
    props.setMediumPriorityFilter(false);
    props.setHighPriorityFilter(false);
    props.setCriticalPriorityFilter(false);
    props.setSelectedAssignee(undefined);
    props.setSelectedAssigneeName('');
    props.setSelectedAssetId(undefined);
    props.setSelectedAssetName?.('');
    if (props.setSelectedUserGroupId) props.setSelectedUserGroupId(undefined);
    if (props.setSelectedTemplateId) props.setSelectedTemplateId(undefined);
    if (props.setSelectedIssueId) props.setSelectedIssueId(undefined);
    if (props.setSelectedLocation) props.setSelectedLocation(undefined);
    props.setSaveFilterToggled(false);
    AsyncStorage.setItem('saveFilterToggled', 'no');
    setOtherPriorityFilter(false);
    setLowPriorityFilter(false);
    setMediumPriorityFilter(false);
    setHighPriorityFilter(false);
    setCriticalPriorityFilter(false);
    setDoneFilter(false);
    setInProgressFilter(false);
    setNotStartedFilter(false);
    setSlaOverdueFilter(false);
    setSelectedAssetId('');
    setSelectedAssetName('');
    setSelectedUserGroupId('');
    setSelectedAssignee('');
    setSelectedTemplateId('');
    props?.resetAllFilters();
    logEvent('TicketsFilter', 'reset');
  };

  const saveFilterButton = () => <View style={{ flexDirection: 'row', alignItems: 'center' }}>
    <Text style={styles.filterToggleText}>{'Save Filter'}</Text>
    <Switch
      toggled={saveFilterToggled}
      toggle={() => {
        logEvent('TicketsFilter', 'save_filter');
        setSaveFilterToggled(!saveFilterToggled);
      }}
      testID='ticket-save-filter-toggle-btn'
    />
  </View>;

  return <Animatable.View
    style={[isMobile ? styles.mobileFilterContainer
      : [
        styles.filterContainer,
        styles.filterShadow,
        { transform: [{ translateX: filterDrawerInset }] },
      ],
    ]}
  >
    {!isMobile && <View style={{ flexDirection: 'row', padding: 24 }}>
      <Text style={styles.filterTitleWeb}>{'Filter tickets'}</Text>
      {saveFilterButton()}
    </View>}
    {props.selectedRange ? <View style={styles.filterDatePickerContainerWeb}>
      {(!isMobile && !props.hideDatePicker) ? datePicker() : <></>}
    </View> : <></>}
    <ScrollView
      nestedScrollEnabled
      ref={scrollView}
      style={styles.filterScrollView}
    >
      <View>
        {!!props.selectedRange && isMobile && datePicker()}
        <View style={styles.filterRow}>
          <Text style={styles.filterText}>{'Priority'}</Text>
          <View style={styles.prioritySection}>
            <CheckBoxItem
              toggled={criticalPriorityFilter}
              toggle={setCriticalPriorityFilter}
              title={`P1 - ${props.slaNames[0] ?? 'Critical'}`}
              {...{ closeDropdowns }}
              testID={'ticket-filter-priority-critical-checkbox'}
            />
            <CheckBoxItem
              toggled={highPriorityFilter}
              toggle={setHighPriorityFilter}
              title={`P2 - ${props.slaNames[1] ?? 'High'}`}
              {...{ closeDropdowns }}
              testID={'ticket-filter-priority-high-checkbox'}
            />
            <CheckBoxItem
              toggled={mediumPriorityFilter}
              toggle={setMediumPriorityFilter}
              title={`P3 - ${props.slaNames[2] ?? 'Moderate'}`}
              {...{ closeDropdowns }}
              testID={'ticket-filter-priority-moderate-checkbox'}
            />
            <CheckBoxItem
              toggled={lowPriorityFilter}
              toggle={setLowPriorityFilter}
              title={`P4 - ${props.slaNames[3] ?? 'Low'}`}
              {...{ closeDropdowns }}
              testID={'ticket-filter-priority-low-checkbox'}
            />
            <CheckBoxItem
              toggled={otherPriorityFilter}
              toggle={setOtherPriorityFilter}
              title={`P5 - ${props.slaNames[4] ?? 'Other'}`}
              {...{ closeDropdowns }}
              testID={'ticket-filter-priority-other-checkbox'}
            />
          </View>
        </View>
        {(props.setDoneFilter || props.setInProgressFilter || props.setNotStartedFilter
          || props.setSlaOverdueFilter) && <View style={styles.filterRow}>
            <View style={{ width: 91 }}>
              <Text style={styles.filterText}>{'Status'}</Text>
            </View>
            <View style={styles.prioritySection}>
              {props.setDoneFilter && <CheckBoxItem
                toggled={doneFilter}
                toggle={setDoneFilter}
                title='Done'
                {...{ closeDropdowns }}
                testID={'ticket-filter-priority-done-checkbox'}
              />}
              {props.setInProgressFilter && <CheckBoxItem
                toggled={inProgressFilter}
                toggle={setInProgressFilter}
                title='In progress'
                {...{ closeDropdowns }}
                testID={'ticket-filter-priority-in-progress-checkbox'}
              />}
              {props.setNotStartedFilter && <CheckBoxItem
                toggled={notStartedFilter}
                toggle={setNotStartedFilter}
                title='Not started'
                {...{ closeDropdowns }}
                textStyle={{ marginEnd: 0 }}
                testID={'ticket-filter-priority-without-status-checkbox'}
              />}
            </View>
          </View>}
        {props.setSlaOverdueFilter && <View
          style={styles.filterRow}
          testID='ticket-filtered-by-sla-label'
        >
          <Text style={styles.filterText}>{'SLA'}</Text>
          <CheckBoxItem
            toggled={slaOverdueFilter}
            toggle={setSlaOverdueFilter}
            title={'SLA overdue'}
            {...{ closeDropdowns }}
            testID={'ticket-filter-sla-overdue-checkbox'}
          />
        </View>}
        <AssigneeDropdown
          {...{
            selectedAssignee,
            selectedAssigneeName,
            setAssigneeDropdownShown,
            setSelectedAssignee,
            setSelectedAssigneeName,
            assigneeDropdownShown,
          }}
          closeOtherDropdowns={() => {
            setAssetDropdownShown(false);
            setUserGroupDropdownShown(false);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          users={props.users}
        />
        <AssetDropdown
          {...{
            selectedAssetId,
            assetDropdownShown,
            assetSearchValue,
            setAssetDropdownShown,
            setAssetSearchValue,
            setSelectedAssetId,
            setSelectedAssetName,
          }}
          closeOtherDropdowns={() => {
            setAssigneeDropdownShown(false);
            setUserGroupDropdownShown(false);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          assets={props.assets}
        />
        {!!props.setSelectedUserGroupId && <UserGroupDropdown
          {...{
            userGroupDropdownShown,
            userGroupSearchValue,
            setUserGroupSearchValue,
            setUserGroupDropdownShown,
            selectedUserGroupId,
            setSelectedUserGroupId,
          }}
          closeOtherDropdowns={() => {
            setAssetDropdownShown(false);
            setAssigneeDropdownShown(false);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          userGroups={props.userGroups}
        />}
        {!!props.setSelectedTemplateId && <TemplateDropdown
          {...{
            templateDropdownShown,
            setTemplateDropdownShown,
            selectedTemplateId,
            setSelectedTemplateId,
          }}
          closeOtherDropdowns={() => {
            setAssetDropdownShown(false);
            setAssigneeDropdownShown(false);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
            setUserGroupDropdownShown(false);
            setIssueDropdownShown(false);
          }}
          templates={props.templates}
        />}
        {(!!props.setSelectedIssueId && selectedTemplateId) && <IssueDropdown
          {...{
            selectedIssueId,
            setSelectedIssueId,
            issueDropdownShown,
            setIssueDropdownShown,
          }}
          closeOtherDropdowns={() => {
            setAssetDropdownShown(false);
            setAssigneeDropdownShown(false);
            setFloorDropdownShown(false);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
            setUserGroupDropdownShown(false);
            setTemplateDropdownShown(false);
          }}
          issues={props.templates.find((template) => template.id
            === selectedTemplateId)?.issues ?? []}
        />}
        {!!props.setSelectedLocation && <>
          <FloorDropdown
            {...{
              selectedFloor,
              selectedLocationId,
              setFloorDropdownShown,
              setSelectedFloor,
              setSelectedArea,
              setSelectedSpace,
              floorDropdownShown,
              setSelectedLocationId,
            }}
            closeOtherDropdowns={() => {
              setAssetDropdownShown(false);
              setAssigneeDropdownShown(false);
              setUserGroupDropdownShown(false);
              setAreaDropdownShown(false);
              setSpaceDropdownShown(false);
            }}
            disabled={props.floorSelectionDisabled}
            locations={buildings
              ? Object.values(buildings[selectedBuildingId]?.floors ?? {}) ?? [] : []}
          />
          {Object.values(selectedFloor?.areas ?? {})?.length > 0 && <AreaDropdown
            {...{
              selectedArea,
              setFloorDropdownShown,
              setAreaDropdownShown,
              setSpaceDropdownShown,
              setSelectedArea,
              setSelectedSpace,
              areaDropdownShown,
              setSelectedLocationId,
            }}
            closeOtherDropdowns={() => {
              setAssetDropdownShown(false);
              setAssigneeDropdownShown(false);
              setUserGroupDropdownShown(false);
              setFloorDropdownShown(false);
              setSpaceDropdownShown(false);
            }}
            floor={selectedFloor}
          />}
          {Object.values(selectedArea?.spaces ?? {}).length > 0 && <SpaceDropdown
            {...{
              selectedSpace,
              setFloorDropdownShown,
              setAreaDropdownShown,
              setSpaceDropdownShown,
              setSelectedSpace,
              setSelectedLocationId,
              spaceDropdownShown,
            }}
            closeOtherDropdowns={() => {
              setAssetDropdownShown(false);
              setAssigneeDropdownShown(false);
              setUserGroupDropdownShown(false);
              setFloorDropdownShown(false);
              setAreaDropdownShown(false);
            }}
            area={selectedArea}
          />}
        </>}
        <View style={{ marginTop: 16 }}>
          <Text style={styles.filterText}>{'Sort'}</Text>
          {slaSort === '' ? <PressableStyled
            style={{ flexDirection: 'row', marginTop: 16 }}
            onPress={() => setSlaSort('asscending')}
            testID='ticket-sort-by-sla-btn'
          >
            <PlusIcon20 />
            <Text style={[
              styles.filterText, { color: Colors.light.hereworksBlue500, marginLeft: 8 },
            ]}>
              {'Apply sorting by SLA'}
            </Text>
          </PressableStyled> : <></>}
          {slaSort ? <View style={Style.row}>
            <Pressable
              style={[Style.dropdown, styles.sortDropdown]}
              onPress={() => {
                setSlaSort(slaSort === 'ascending' ? 'descending' : 'ascending');
              }}>
              {slaSort === 'ascending' ? <AscendingIcon /> : <DescendingIcon />}
              <Text style={[Style.dropdownText, styles.sortDropdownText]}>
                {slaSort === 'ascending' ? 'Ascending' : 'Descending'}
              </Text>
              <FontAwesome
                size={20}
                style={{ color: Colors.light.hereworksBlue500 }}
                name={slaSort === 'ascending' ? 'angle-up' : 'angle-down'}
              />
            </Pressable>
            <Pressable
              onPress={() => setSlaSort('')}
              testID='ticket-close-sort-by-sla-btn'
            >
              <DeleteIcon24 />
            </Pressable>
          </View> : <></>}
        </View>
      </View>
      <View style={styles.buttonContainer}>
        {anyFilterApplied && <ResetFiltersButton
          testID='ticket-filter-reset-btn'
          onPress={resetFilters}
        />}
        {isMobile ? <></> : <PressableStyled
          style={styles.cancelButton}
          onPress={() => {
            props.setFilterOpen(false);
            logEvent('TicketsFilter', 'cancel');
          }}
        >
          <Text style={styles.cancelButtonText}>{'Cancel'}</Text>
        </PressableStyled>}
        {isMobile ? <View style={{ alignItems: 'center', marginBottom: 16 }}>{saveFilterButton()}</View> : <></>}
        <PressableStyled
          style={styles.applyButton}
          onPress={() => {
            props.setFilterOpen(false);
            props.setTickets([]);
            props.setSelectedTicket(undefined);
            props.setOtherPriorityFilter(otherPriorityFilter);
            props.setLowPriorityFilter(lowPriorityFilter);
            props.setMediumPriorityFilter(mediumPriorityFilter);
            props.setHighPriorityFilter(highPriorityFilter);
            props.setCriticalPriorityFilter(criticalPriorityFilter);
            if (props.setDoneFilter) props.setDoneFilter(doneFilter);
            if (props.setInProgressFilter) props.setInProgressFilter(inProgressFilter);
            if (props.setNotStartedFilter) props.setNotStartedFilter(notStartedFilter);
            if (props.setSlaOverdueFilter) props.setSlaOverdueFilter(slaOverdueFilter);
            props.setSelectedAssignee(selectedAssignee);
            props.setSelectedAssigneeName(selectedAssigneeName);
            props.setSelectedAssetId(selectedAssetId);
            props.setSelectedAssetName?.(selectedAssetName);
            if (props.setSelectedUserGroupId) props.setSelectedUserGroupId(selectedUserGroupId);
            if (props.setSelectedTemplateId) props.setSelectedTemplateId(selectedTemplateId);
            if (props.setSelectedIssueId) props.setSelectedIssueId(selectedIssueId);
            if (props.setSelectedLocation) props.setSelectedLocation(selectedLocationId);
            if (selectedFloor) props.setSelectedFloor(selectedFloor);
            props.setSaveFilterToggled(saveFilterToggled);
            props.setSlaSort(slaSort);
            AsyncStorage.setItem('saveFilterToggled', saveFilterToggled ? 'yes' : 'no');
            if (saveFilterToggled) {
              dispatch(updateTicketsFilter({
                otherPriority: otherPriorityFilter,
                lowPriority: lowPriorityFilter,
                mediumPriority: mediumPriorityFilter,
                highPriority: highPriorityFilter,
                criticalPriority: criticalPriorityFilter,
                doneFilter,
                inProgressFilter,
                notStartedFilter,
                selectedAssignee,
                selectedLocation: selectedLocationId,
                selectedAssetId,
                selectedUserGroupId,
                slaOverdueFilter,
                slaSort,
                selectedDateRange: date,
                selectedTemplateId,
                selectedIssueId,
              }));
            }
            if (props.setSelectedRange) props.setSelectedRange(dateRange);
            if (props.onConfirm) props.onConfirm(date.start, date.end);
            logEvent('TicketsFilter', 'apply');
          }}
          testID='ticket-filter-apply-btn'
        >
          <Text style={styles.applyText}>{'Apply'}</Text>
        </PressableStyled>
      </View>
    </ScrollView>
  </Animatable.View>;
}

const styles = StyleSheet.create({
  filterText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    marginBottom: 4,
    marginEnd: 20,
    color: Colors.light.hereworksBlack600,
  },
  filterShadow: {
    shadowOffset: {
      width: 6,
      height: 6,
    },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    shadowColor: 'black',
    elevation: 5,
  },
  filterContainer: {
    flex: 1,
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#fff',
    paddingBottom: 24,
    width: 480,
  },
  mobileFilterContainer: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 10,
    zIndex: 2,
    paddingBottom: 24,
  },
  datePickerContainer: {
    flex: 1,
    zIndex: 2,
    marginTop: isMobile ? undefined : 24,
  },
  filterDatePickerContainerWeb: {
    paddingHorizontal: 24,
    zIndex: 2,
  },
  filterScrollView: {
    flexGrow: 1,
    paddingHorizontal: 24,
  },
  filterRow: {
    marginTop: 13,
  },
  applyText: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
  },
  cancelButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksLightBlue,
    alignItems: 'center',
    marginBottom: 8,
    marginTop: 32,
  },
  cancelButtonText: {
    color: Colors.light.hereworksLightBlue,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
  },
  applyButton: {
    backgroundColor: Colors.light.hereworksLightBlue,
    borderRadius: 4,
    alignItems: 'center',
  },
  buttonContainer: {
    marginTop: 24,
  },
  prioritySection: {
    flexDirection: 'row',
    flex: 1,
    flexWrap: 'wrap',
    paddingRight: 10,
  },
  filterTitleWeb: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  filterToggleText: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    marginRight: 8,
  },
  sortDropdown: {
    zIndex: -1,
    justifyContent: 'space-between',
    width: undefined,
    flex: 1,
    paddingHorizontal: 10,
    marginEnd: 8,
  },
  sortDropdownText: {
    flex: 1,
    marginHorizontal: 8,
  },
  resetFiltersButton: {
    marginBottom: 12,
  },
  resetFiltersLabel: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_500Medium',
    textTransform: 'uppercase',
    textDecorationLine: 'underline',
  },
});
